import React from 'react';

import styled from '@emotion/styled';

import { InputSizeProp } from './helpers/inputSizing';
import HelperText from './HelperText';
import Input, { InputProps } from './Input';
import Label from './Label';

const FieldElement = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * When using a component that is not <Input> then <Field> expects you to tell
 * it what props it should be expecting: <Field<MyCoolCustomInputProps>
 * input={{...props for MyCoolCustomInput... }} />
 */
type FieldProps<T extends { id: string; error?: boolean } = InputProps> = {
  helperText?: React.ReactNode;
  errorText?: React.ReactNode;
  label: React.ReactNode;
  /**
   * The component to use in the input slot. Defaults to a plain text Input
   */
  InputComponent?: React.FC<T>;
  /**
   * Props to be passed to InputComponent, must have id:string and error?: boolean
   */
  input: T;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  disabled?: boolean;
  sizeVariant?: InputSizeProp;
};

const Field = <T extends { id: string; error?: boolean; labelId?: string } = InputProps>({
  label,
  helperText,
  errorText,
  disabled,
  sizeVariant,
  InputComponent,
  input,
}: FieldProps<T>) => {
  const IComponent = InputComponent ?? Input;
  const labelId = `${input.id}-label`;
  return (
    <FieldElement>
      <Label
        htmlFor={input.id}
        id={labelId}
        error={input.error}
        sizeVariant={sizeVariant}
        data-has-error={input.error}
      >
        {label}
      </Label>
      {helperText ? (
        <div style={{ marginTop: '4px' }}>
          <HelperText error={input.error}>{helperText}</HelperText>
        </div>
      ) : null}
      <div style={{ marginTop: '16px' }}>
        <IComponent
          labelId={labelId}
          error={input.error}
          disabled={disabled}
          sizeVariant={sizeVariant}
          {...input}
        />
      </div>
      {input.error && errorText ? (
        <div style={{ marginTop: '12px' }}>
          <HelperText error>{errorText}</HelperText>
        </div>
      ) : null}
    </FieldElement>
  );
};

export default Field;
